import styled from 'styled-components';
import Button from '../../../components/Button';
import WidthContainer from '../../../components/WidthContainer';
import TextInput from '../../../components/Inputs/Text';
import { useLoginWithEmail } from '@privy-io/react-auth';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import VerificationCodeInput from '../../../components/VerificationCodeInput';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  gap: 24px;
  position: relative;
`;

const BackContainer = styled.div`
  position: absolute;
  top: calc(16px + env(safe-area-inset-top));
  left: 0px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 8px;
`;

const Logo = styled.img`
  width: 200px;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 38px;
  font-weight: 600;
  line-height: 40px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.primary.primary};
`;

const Subtitle = styled.h2`
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.text.title};
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const MobileLogin = () => {
  const [status, setStatus] = useState('awaiting-email-input');
  const { sendCode, state, loginWithCode } = useLoginWithEmail();

  const otpRef = useRef(null);

  const navigate = useNavigate();

  const handleLoginWithCode = async (code) => {
    try {
      await loginWithCode({ code });
      navigate(-1);
    } catch (error) {
      toast.error('Invalid code');
      if (otpRef.current) {
        otpRef.current.clear();
      }
    }
  };

  const emailSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const email = watch('email');

  const handleSendCode = async (values) => {
    await sendCode({ email: values.email });
    setStatus('awaiting-code-input');
  };

  return (
    <WidthContainer>
      <Container>
        {status === 'awaiting-code-input' && (
          <BackContainer
            onClick={() => {
              setStatus('awaiting-email-input');
              setValue('email', '');
            }}
          >
            <img src='/chevron-left.svg' width={16} />
          </BackContainer>
        )}

        {status === 'awaiting-email-input' && (
          <>
            <Logo src='/iconLogo.svg' />
            <ContentContainer>
              <TitleContainer>
                <Title>Welcome to Deks</Title>
                <Subtitle>The easiest way to gift crypto</Subtitle>
              </TitleContainer>
              <FormContainer onSubmit={handleSubmit(handleSendCode)}>
                <Controller
                  name='email'
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextInput
                      inputMode='email'
                      placeholder='your@email.com'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  )}
                />
                <Button
                  loading={state?.status === 'sending-code'}
                  fullWidth={true}
                  disabled={!isValid}
                >
                  Continue
                </Button>
              </FormContainer>
            </ContentContainer>
          </>
        )}
        {status === 'awaiting-code-input' && (
          <>
            <ContentContainer>
              <TitleContainer>
                <Title>Verification code</Title>
                <Subtitle>
                  Please check {email} for an email from privy.io and enter your
                  code below.
                </Subtitle>
              </TitleContainer>
              <FormContainer>
                <VerificationCodeInput
                  ref={otpRef}
                  length={6}
                  loading={state?.status === 'submitting-code'}
                  onComplete={(code) => handleLoginWithCode(code)}
                  size='md'
                  autoFocus
                />
              </FormContainer>
            </ContentContainer>
          </>
        )}
      </Container>
    </WidthContainer>
  );
};

export default MobileLogin;
